import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { logger } from '@/logger';

import type {
	EditableCompetitorArrayField,
	EditableCompetitorField,
	SocialPlatformKey,
} from '../../../../bridge/types/competitor-fields.ts';
import type {
	Competitor,
	CompetitorInsight,
	CompetitorProduct,
} from '../../../../bridge/types/competitors.ts';

import { hasSubstantiveData } from './tabs/competitorData.ts';

const ANALYZE_POLL_INTERVAL_MS = 5_000;
const ANALYZE_MAX_POLL_ATTEMPTS = 18;
// From-scratch research (sparse seed → medium reasoning effort) routinely runs
// 60–180s — well past the 90s IG-analyze window. Give the research poll a much
// longer ceiling so a slow-but-successful enrichment isn't mistaken for a
// failure and frozen in the "couldn't find much" state before its data lands.
const RESEARCH_MAX_POLL_ATTEMPTS = 60;

// The composer accepts a single free-text seed that is either a business name
// or a website. A scheme (http://, https://) or a bare "host.tld" with no
// whitespace reads as a URL; anything else (including multi-word names) is a
// name.
export function classifyQuery(q: string): 'name' | 'url' {
	const trimmed = q.trim();
	if (!trimmed) return 'name';
	if (/^[a-z][\w+.-]*:\/\//i.test(trimmed)) return 'url';
	if (/\s/.test(trimmed)) return 'name';
	return /^[^\s.]+\.[^\s.]{2,}/.test(trimmed) ? 'url' : 'name';
}

// Turns a raw URL-ish seed into a canonical origin+path: prepend https:// when
// no scheme is present, lowercase the host, and drop query/fragment so the
// seeded website_url is stable regardless of how the user pasted it.
export function normalizeUrl(q: string): string {
	const trimmed = q.trim();
	const withScheme = /^[a-z][\w+.-]*:\/\//i.test(trimmed)
		? trimmed
		: `https://${trimmed}`;
	try {
		const u = new URL(withScheme);
		u.hostname = u.hostname.toLowerCase();
		const path = u.pathname.replace(/\/+$/, '');
		return `${u.protocol}//${u.hostname}${path}`;
	} catch {
		return withScheme;
	}
}

export type UseCompetitorsApi = {
	competitors: Record<string, Competitor>;
	insightsMap: Record<string, CompetitorInsight[]>;
	loading: boolean;
	error: string | null;
	analyzingIds: Set<string>;
	researchingIds: Set<string>;
	researchedIds: Set<string>;
	failedResearchIds: Set<string>;
	isResearching: boolean;
	refetch: () => Promise<void>;
	onAddCompetitor: () => Promise<string | null>;
	onResearchCompetitor: (query: string) => Promise<string | null>;
	onUpdateCompetitor: (
		id: string,
		field: EditableCompetitorField,
		value: string,
	) => Promise<void>;
	onUpdateCompetitorArrayField: (
		id: string,
		field: EditableCompetitorArrayField,
		value: string[],
	) => Promise<void>;
	onUpdateCompetitorSocialUrl: (
		id: string,
		platform: SocialPlatformKey,
		url: string,
	) => Promise<void>;
	onUpdateCompetitorProducts: (
		id: string,
		products: CompetitorProduct[],
	) => Promise<void>;
	onRemoveCompetitor: (id: string) => Promise<void>;
	onRunInsights: (id: string) => Promise<void>;
};

export function useCompetitorsApi(userId: string | null): UseCompetitorsApi {
	const bridgewayApi = useBridgewayApi();
	const [competitors, setCompetitors] = useState<Record<string, Competitor>>(
		{},
	);
	const [insightsMap, setInsightsMap] = useState<
		Record<string, CompetitorInsight[]>
	>({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [analyzingIds, setAnalyzingIds] = useState<Set<string>>(new Set());
	const [researchingIds, setResearchingIds] = useState<Set<string>>(new Set());
	const [researchedIds, setResearchedIds] = useState<Set<string>>(new Set());
	const [failedResearchIds, setFailedResearchIds] = useState<Set<string>>(
		new Set(),
	);
	const competitorsRef = useRef<Record<string, Competitor>>({});
	competitorsRef.current = competitors;
	const insightsMapRef = useRef<Record<string, CompetitorInsight[]>>({});
	insightsMapRef.current = insightsMap;
	const patchQueueRef = useRef<Record<string, Promise<void>>>({});
	const fetchedInsightsRef = useRef<Set<string>>(new Set());
	const analyzeTimeoutsRef = useRef<
		Map<string, ReturnType<typeof setInterval>>
	>(new Map());

	useEffect(() => {
		const timeouts = analyzeTimeoutsRef.current;
		return () => {
			for (const timeout of timeouts.values()) clearInterval(timeout);
			timeouts.clear();
		};
	}, []);

	const fetchCompetitors = useCallback(async () => {
		if (!userId) return;
		try {
			const data = await bridgewayApi.get<Competitor[]>(
				`/api/user/${userId}/competitors/`,
			);
			const byId: Record<string, Competitor> = {};
			for (const c of data) byId[c.id] = c;
			setCompetitors(byId);
			// Clear only once fresh data has landed. Clearing before the request
			// returns would blank the error mid-retry and briefly show the empty
			// state while the list is still empty and the fetch is in flight.
			setError(null);
		} catch (err) {
			logger.error('Failed to fetch competitors', err);
			setError('We couldn’t load your similar businesses.');
		} finally {
			setLoading(false);
		}
	}, [userId, bridgewayApi]);

	const fetchInsights = useCallback(
		async (competitorId: string) => {
			if (!userId) return;
			try {
				const data = await bridgewayApi.get<CompetitorInsight[]>(
					`/api/user/${userId}/competitors/${competitorId}/insights`,
				);
				setInsightsMap((prev) => ({ ...prev, [competitorId]: data }));
			} catch {
				// Insights are optional
			}
		},
		[userId, bridgewayApi],
	);

	useEffect(() => {
		void fetchCompetitors();
	}, [fetchCompetitors]);

	const competitorIdsKey = Object.keys(competitors).sort().join(',');
	useEffect(() => {
		const currentIds = Object.keys(competitorsRef.current);
		for (const id of currentIds) {
			if (fetchedInsightsRef.current.has(id)) continue;
			fetchedInsightsRef.current.add(id);
			void fetchInsights(id);
		}
		const currentIdSet = new Set(currentIds);
		for (const id of fetchedInsightsRef.current) {
			if (!currentIdSet.has(id)) fetchedInsightsRef.current.delete(id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [competitorIdsKey]);

	// Polls /analyze for a single competitor until `isComplete` returns true (or
	// the attempt cap is hit), refetching insights + competitors each tick so the
	// caller's completion check sees fresh state. Owns all the interval
	// bookkeeping; the caller decides what "done" means and what to do on done.
	const pollAnalyze = useCallback(
		(
			id: string,
			opts: {
				isComplete: () => boolean;
				onDone: () => void;
				maxAttempts?: number;
			},
		) => {
			const maxAttempts = opts.maxAttempts ?? ANALYZE_MAX_POLL_ATTEMPTS;
			const existing = analyzeTimeoutsRef.current.get(id);
			if (existing) clearInterval(existing);
			let attempts = 0;
			const intervalId = setInterval(() => {
				attempts += 1;
				void (async () => {
					await Promise.all([fetchInsights(id), fetchCompetitors()]);
					if (opts.isComplete() || attempts >= maxAttempts) {
						clearInterval(intervalId);
						analyzeTimeoutsRef.current.delete(id);
						fetchedInsightsRef.current.delete(id);
						opts.onDone();
					}
				})();
			}, ANALYZE_POLL_INTERVAL_MS);
			analyzeTimeoutsRef.current.set(id, intervalId);
		},
		[fetchInsights, fetchCompetitors],
	);

	const patchCompetitor = useCallback(
		async (id: string, data: Partial<Competitor>) => {
			if (!userId) return;
			// optimistic
			setCompetitors((prev) => ({
				...prev,
				[id]: { ...(prev[id] ?? { id, name: '' }), ...data },
			}));
			// Serialize PATCHes per competitor: backend PATCH is
			// read-modify-write, so parallel requests race and lose writes.
			const prev = patchQueueRef.current[id] ?? Promise.resolve();
			const next: Promise<void> = prev.then(async () => {
				try {
					await bridgewayApi.patch<Competitor>(
						`/api/user/${userId}/competitors/${id}`,
						data,
					);
				} catch (err) {
					logger.error('Failed to patch competitor', err);
					void fetchCompetitors();
				} finally {
					if (patchQueueRef.current[id] === next) {
						delete patchQueueRef.current[id];
					}
				}
			});
			patchQueueRef.current[id] = next;
			await next;
		},
		[userId, bridgewayApi, fetchCompetitors],
	);

	const onUpdateCompetitor = useCallback(
		async (id: string, field: EditableCompetitorField, value: string) => {
			await patchCompetitor(id, { [field]: value } as Partial<Competitor>);
		},
		[patchCompetitor],
	);

	const onUpdateCompetitorArrayField = useCallback(
		async (
			id: string,
			field: EditableCompetitorArrayField,
			value: string[],
		) => {
			await patchCompetitor(id, { [field]: value } as Partial<Competitor>);
		},
		[patchCompetitor],
	);

	const onUpdateCompetitorSocialUrl = useCallback(
		async (id: string, platform: SocialPlatformKey, url: string) => {
			const current = competitorsRef.current[id]?.social_media ?? {};
			const next = {
				...current,
				[platform]: { ...(current[platform] ?? {}), url },
			};
			await patchCompetitor(id, { social_media: next });
		},
		[patchCompetitor],
	);

	const onUpdateCompetitorProducts = useCallback(
		async (id: string, products: CompetitorProduct[]) => {
			await patchCompetitor(id, { products });
		},
		[patchCompetitor],
	);

	const onRemoveCompetitor = useCallback(
		async (id: string) => {
			if (!userId) return;
			setCompetitors((prev) => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
			// Drain pending PATCHes for this id so a queued PATCH can't land
			// after DELETE and resurrect state via the catch-path refetch.
			await patchQueueRef.current[id]?.catch(() => {});
			delete patchQueueRef.current[id];
			try {
				await bridgewayApi.delete(`/api/user/${userId}/competitors/${id}`);
			} catch (err) {
				logger.error('Failed to delete competitor', err);
				void fetchCompetitors();
			}
		},
		[userId, bridgewayApi, fetchCompetitors],
	);

	const onAddCompetitor = useCallback(async (): Promise<string | null> => {
		if (!userId) return null;
		const id = crypto.randomUUID();
		const newComp: Competitor = { id, name: '' };
		setCompetitors((prev) => ({ ...prev, [id]: newComp }));
		try {
			await bridgewayApi.put(`/api/user/${userId}/competitors/${id}`, newComp);
			return id;
		} catch (err) {
			logger.error('Failed to add competitor', err);
			void fetchCompetitors();
			return null;
		}
	}, [userId, bridgewayApi, fetchCompetitors]);

	// Seeds a competitor from a single name/URL query, kicks off the same
	// /analyze pipeline the manual Add flow uses, and polls until the backend
	// stamps last_researched_at. While polling the card shows a researching
	// skeleton; on completion it becomes a normal card, or a "thin" card if no
	// substantive data came back.
	const onResearchCompetitor = useCallback(
		async (query: string): Promise<string | null> => {
			if (!userId) return null;
			const id = crypto.randomUUID();
			const kind = classifyQuery(query);
			const seed: Competitor = {
				id,
				name: kind === 'name' ? query.trim() : '',
				website_url: kind === 'url' ? normalizeUrl(query) : undefined,
			};
			setCompetitors((prev) => ({ ...prev, [id]: seed }));
			try {
				await bridgewayApi.put(`/api/user/${userId}/competitors/${id}`, seed);
				await bridgewayApi.post(
					`/api/user/${userId}/competitors/${id}/analyze`,
					{},
				);
				setResearchingIds((prev) => new Set([...prev, id]));
				pollAnalyze(id, {
					maxAttempts: RESEARCH_MAX_POLL_ATTEMPTS,
					isComplete: () =>
						competitorsRef.current[id]?.last_researched_at != null,
					onDone: () => {
						setResearchingIds((prev) => {
							const next = new Set(prev);
							next.delete(id);
							return next;
						});
						setResearchedIds((prev) => new Set([...prev, id]));
						if (!hasSubstantiveData(competitorsRef.current[id] ?? seed)) {
							setFailedResearchIds((prev) => new Set([...prev, id]));
						}
					},
				});
				return id;
			} catch (err) {
				logger.error('Failed to research competitor', err);
				setResearchingIds((prev) => {
					const next = new Set(prev);
					next.delete(id);
					return next;
				});
				setFailedResearchIds((prev) => new Set([...prev, id]));
				void fetchCompetitors();
				return null;
			}
		},
		[userId, bridgewayApi, fetchCompetitors, pollAnalyze],
	);

	const onRunInsights = useCallback(
		async (id: string) => {
			if (!userId) return;
			setAnalyzingIds((prev) => new Set([...prev, id]));
			const startedAt = competitorsRef.current[id]?.last_analyzed_at ?? null;
			const baselineInsightCount = insightsMapRef.current[id]?.length ?? 0;
			const removeFromAnalyzingIds = () =>
				setAnalyzingIds((prev) => {
					const next = new Set(prev);
					next.delete(id);
					return next;
				});
			try {
				await bridgewayApi.post(
					`/api/user/${userId}/competitors/${id}/analyze`,
					{},
				);
				pollAnalyze(id, {
					isComplete: () => {
						const after = competitorsRef.current[id]?.last_analyzed_at ?? null;
						const newCount = insightsMapRef.current[id]?.length ?? 0;
						return (
							(!!after && after !== startedAt) ||
							newCount > baselineInsightCount
						);
					},
					onDone: removeFromAnalyzingIds,
				});
			} catch (err) {
				logger.error('Failed to analyze competitor', err);
				removeFromAnalyzingIds();
			}
		},
		[userId, bridgewayApi, pollAnalyze],
	);

	return {
		competitors,
		insightsMap,
		loading,
		error,
		analyzingIds,
		researchingIds,
		researchedIds,
		failedResearchIds,
		isResearching: analyzingIds.size > 0,
		refetch: fetchCompetitors,
		onAddCompetitor,
		onResearchCompetitor,
		onUpdateCompetitor,
		onUpdateCompetitorArrayField,
		onUpdateCompetitorSocialUrl,
		onUpdateCompetitorProducts,
		onRemoveCompetitor,
		onRunInsights,
	};
}
